import { useMemo, useRef } from "react";

import classnames from "classnames";
import styled from "styled-components/macro";

import { useChartSettings } from "components/multiphase-chart/context";
import { useOverflowIndex } from "components/multiphase-chart/hooks";
import useGlobalNormalizeBy from "components/multiphase-chart/hooks/useGlobalNormalizeBy";
import { visibilityToggleStyles } from "components/shared/SharedStyles";
import { useChartDependencies } from "components/shared/charts/hooks";

import { EntityKind } from "../../../../models/entityKind";
import {
  DevDebugToggle,
  ForecastToggle,
  FullscreenToggle,
  LegendToggle,
  NormalizeToggle,
  Popout,
  ProducingToggle,
  ScreenshotToggle
} from "../settings";
import BackfitToggle from "../settings/toggles/forecast/BackfitToggle";
import OverflowToggle from "../settings/toggles/overflow/OverflowToggle";
import ToolbarHeader from "./ToolbarHeader";

const Divider = styled.div`
  width: 1px;
  height: var(--chart-toolbar-height);
  background-color: #d9e1e2;
`;

/**
 * Applies an instance's `hiddenToolbarKeys`. Shared with the overflow menu,
 * which slices the same array and would otherwise show what the strip hides.
 */
export const visibleToolbarComponents = (
  toolbarComponents: JSX.Element[],
  hiddenKeys: Maybe<string[]>
): JSX.Element[] =>
  !hiddenKeys || hiddenKeys.length === 0
    ? toolbarComponents
    : toolbarComponents.filter((c) => !hiddenKeys.includes(String(c.key)));

export const components = [
  <NormalizeToggle key="NormalizeToggle" />,
  <Divider key="globalSettingsDivider" />,
  <DevDebugToggle key="debugModeToggle" />,
  <ProducingToggle key="ProducingToggle" />,
  <ForecastToggle key="ForecastToggle" />,
  <BackfitToggle key="BackfitToggle" />,
  <ScreenshotToggle key="ScreenshotToggle" />,
  <LegendToggle key="LegendToggle" />,
  <Popout key="Popout" />,
  <FullscreenToggle key="FullscreenToggle" />
];

// `uwi` pins the strip to the chart's own well. Dashboard widgets pass nothing.
const ChartToolbar = ({ uwi = undefined }: { uwi?: string }) => {
  const ref = useRef<HTMLDivElement>(null);

  useGlobalNormalizeBy();
  useOverflowIndex(ref);

  const [chartSettings] = useChartSettings();
  const { selectedWells } = useChartDependencies(EntityKind.Well);
  const overflowIndex = chartSettings.overflowIndex;

  const visibleComponents = useMemo(() => {
    // Remove fullscreen option if chart is in read-only mode
    const readOnlyFiltered = chartSettings?.isReadOnly
      ? components.filter((c) => c.key !== "FullscreenToggle")
      : components;

    const filteredComponents = visibleToolbarComponents(
      readOnlyFiltered,
      chartSettings?.hiddenToolbarKeys
    );

    let visible = filteredComponents.slice(
      0,
      overflowIndex === filteredComponents.length - 1 ? overflowIndex + 1 : overflowIndex
    );

    // Remove divider if it is the last element
    if (visible[visible.length - 1]?.key === "globalSettingsDivider") {
      visible = visible.slice(0, visible.length - 1);
    }

    return visible;
  }, [chartSettings]);

  // A pinned chart plots its own well whether or not anything is selected on
  // the map, so an empty selection must not hide its toolbar.
  const visible =
    chartSettings.isMouseOver && (!!uwi || Object.keys(selectedWells).length > 0);
  const wrapperClassnames = classnames({ visible });

  return (
    <Wrapper ref={ref} className={wrapperClassnames} data-testid="chart-toolbar">
      <ToolbarHeader />

      <ToolbarContent>
        <GapWrapper>{visibleComponents.map((component) => component)}</GapWrapper>

        {
          /* more-toggle for overflowing toolbar icons
            don't display for popped out charts*/
          !chartSettings?.isReadOnly && <OverflowToggle />
        }
      </ToolbarContent>
    </Wrapper>
  );
};

export default ChartToolbar;

const GapWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%), inset 0 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 10;
  padding-right: 4px;
`;

const ToolbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding-right: 4px;
`;
